import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-react";
import { Seo } from "../components/layout/Seo";
import { PageHero } from "../components/layout/PageHero";
import { Eyebrow, Reveal } from "../components/motion/Reveal";
import { ContactSection } from "../components/home/ContactSection";
import { getService, services } from "../content/services";

const CTA = { quote: "Request a quote", site_survey: "Request a site survey", consultation: "Request a consultation" };

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/services" replace />;
  const idx = services.indexOf(s);
  const next = services[(idx + 1) % services.length];
  const alt = s.cta === "quote" ? "site_survey" : "quote";

  return (
    <>
      <Seo title={`${s.name} | TechCare Technologies`} description={s.summary.slice(0, 158)} path={`/services/${s.slug}`} />
      <PageHero eyebrow={`Service ${s.index} / 08`} lines={[s.name]} lead={s.summary} image={s.image} imageAlt={s.imageAlt} testid="service-hero">
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Link to={{ search: `?type=${s.cta}`, hash: "#service-contact" }} data-testid="service-primary-cta" className="tc-btn-primary inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">{CTA[s.cta]}</Link>
          <Link to={{ search: `?type=${alt}`, hash: "#service-contact" }} data-testid="service-secondary-cta" className="inline-flex h-12 items-center justify-center rounded-full border border-orange-500/50 px-7 text-sm font-semibold text-orange-300 transition-colors hover:bg-orange-500/10">{CTA[alt]}</Link>
          <Link to="/services" className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-7 text-sm text-stone-200 hover:border-orange-500">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>
        </div>
      </PageHero>

      <section className="bg-[#FAFAF9] py-20 text-stone-900 sm:py-28" data-testid="service-capabilities">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <Eyebrow light>What's included</Eyebrow>
          <div className="mt-10 grid gap-px overflow-hidden rounded-[22px] border border-stone-300 bg-stone-300 sm:grid-cols-2 lg:grid-cols-3">
            {s.capabilities.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.06} className="bg-[#FAFAF9] p-8">
                <p className="font-mono text-xs text-orange-700">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-6 font-display text-xl">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{d}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 grid gap-10 lg:grid-cols-12" data-testid="service-applications">
            <div className="lg:col-span-4">
              <Eyebrow light>Who it helps</Eyebrow>
              <ul className="mt-6 flex flex-wrap gap-2">
                {(s.audience ?? []).map((a) => (
                  <li key={a} className="rounded-full border border-stone-300 bg-white px-3.5 py-1.5 text-sm text-stone-700">{a}</li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-8">
              <Eyebrow light>Practical applications</Eyebrow>
              <ol className="mt-6 grid gap-3 sm:grid-cols-2">
                {(s.applications ?? []).map((a, i) => (
                  <Reveal as="li" key={a} delay={(i % 2) * 0.06} className="flex gap-4 rounded-2xl border border-stone-300 bg-white p-5 text-[15px] leading-relaxed text-stone-700">
                    <span className="font-mono text-xs text-orange-700">E{i + 1}</span> {a}
                  </Reveal>
                ))}
              </ol>
              <p className="mt-3 text-xs text-stone-500">Illustrative examples of how this service can be applied.</p>
            </div>
          </div>
          <div className="mt-16 grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4"><Eyebrow light>What you can expect</Eyebrow></div>
            <ul className="space-y-4 lg:col-span-8">
              {s.outcomes.map((o) => (
                <li key={o} className="flex items-center gap-4 border-b border-stone-300 pb-4 font-display text-xl sm:text-2xl">
                  <Check className="h-5 w-5 shrink-0 text-orange-700" /> {o}
                </li>
              ))}
            </ul>
          </div>
          {s.note && (
            <p className="mt-10 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900" data-testid="service-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" /> {s.note}
            </p>
          )}
        </div>
      </section>

      <Link to={`/services/${next.slug}`} data-testid="service-next-link"
        className="group block border-y border-white/10 bg-[#0C0A09] px-5 py-14 text-stone-100 sm:px-8 lg:px-12">
        <span className="mx-auto flex max-w-[1440px] items-center justify-between gap-6">
          <span>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-stone-400">Next service</span>
            <span className="mt-3 block font-display text-3xl tracking-tight transition-colors duration-300 group-hover:text-orange-400 sm:text-5xl">{next.name}</span>
          </span>
          <ArrowRight className="h-8 w-8 shrink-0 text-orange-400 transition-transform duration-500 group-hover:translate-x-2" />
        </span>
      </Link>

      <ContactSection id="service-contact" eyebrow={`Enquire about ${s.name}`} defaultService={s.name} defaultType={s.cta} />
    </>
  );
}
